import { IAMDashboardState } from '@/modules/amDashboard/store/state';
import { IRootState } from '@/store/state';
import { ActionContext, ActionTree } from 'vuex';
import { AMDashboardService } from '@/modules/amDashboard/services';
import * as AT from './actionTypes';
import * as MT from './mutationsTypes';
import SubmissionRequest from '@/models/SubmissionRequest';
import {
  OrgSubmissionsRequest,
  PaginatedResult,
  OrganisationClientRequest,
  DownloadSubmissionRequestsResponse,
  FetchSubmissionRequestsUrlResponse,
  AutomatchTemplatesRequest,
  ExtractTemplateContextResponse
} from '../types';
import { SET_LOADER } from '../../organisation/store/mutationTypes';
import { PensionScheme } from '../../pensionScheme/types';
import { AuthorizationLetterResponse } from '../types';
import { Paginated } from '@/modules/analysis/types';
/**
 * --------------------------------------------------
 *  Dashboard Actions
 * --------------------------------------------------
 **/
export const AMDashboardActions: IAMDashboardActions = {
  [AT.FETCH_SUBMISSION_REQUESTS]: (
    { commit },
    orgSubmissionsRequest: OrgSubmissionsRequest
  ): Promise<SubmissionRequest[]> => {
    commit(SET_LOADER, true);
    return AMDashboardService.getSubmissionRequests(orgSubmissionsRequest).then(
      (paginatedResult: PaginatedResult) => {
        commit(MT.SET_PAGINATION_META, paginatedResult.meta);
        commit(MT.SET_SUBMISSION_REQUESTS, paginatedResult.data);
        commit(SET_LOADER, false);
        return paginatedResult.data;
      }
    );
  },
  [AT.FETCH_ORGANISATION_CLIENTS]: (
    { commit },
    params: OrganisationClientRequest
  ): Promise<PensionScheme[]> => {
    commit(SET_LOADER, true);
    return AMDashboardService.getOrganizationClients(params).then(
      (organizationClients: PensionScheme[]) => {
        commit(MT.SET_ORGANISATION_CLIENTS, organizationClients);
        commit(SET_LOADER, false);
        return organizationClients;
      }
    );
  },
  [AT.DOWNLOAD_AUTHORIZATION_LETTER]: (
    { commit },
    submissionRequestId: string
  ): Promise<AuthorizationLetterResponse> => {
    return AMDashboardService.downloadAuthorizationLetter(
      submissionRequestId
    ).then((response: AuthorizationLetterResponse) => {
      return response;
    });
  },

  [AT.DOWNLOAD_SUBMISSION_REQUESTS]: (
    { commit },
    organizationId: string
  ): Promise<DownloadSubmissionRequestsResponse> => {
    return AMDashboardService.downloadSubmissionRequests(organizationId).then(
      (response: DownloadSubmissionRequestsResponse) => {
        return response;
      }
    );
  },

  [AT.FETCH_SUBMISSION_REQUESTS_URL]: (
    { commit },
    jobId: string
  ): Promise<FetchSubmissionRequestsUrlResponse> => {
    return AMDashboardService.fetchSubmissionRequestsUrl(jobId).then(
      (response: FetchSubmissionRequestsUrlResponse) => {
        return response;
      }
    );
  },

  [AT.SEARCH_FOR_SUBMISSION_REQUESTS]: (
    { commit },
    orgSubmissionsRequest: OrgSubmissionsRequest
  ): Promise<Paginated<SubmissionRequest>> => {
    return AMDashboardService.getSubmissionRequests(orgSubmissionsRequest);
  },

  [AT.AUTO_MATCH_SUBMISSIONS]: async (
    { commit },
    orgAutomatchTemplatesRequest: AutomatchTemplatesRequest
  ): Promise<{
    autoMatchData: Record<string, SubmissionRequest>;
    templateContextData: ExtractTemplateContextResponse;
  }> => {
    return await AMDashboardService.autoMatchTemplates(
      orgAutomatchTemplatesRequest
    ).then(paginatedResult => {
      return paginatedResult;
    });
  }
};

/**
 * --------------------------------------------------
 *  Dashboard Action Context Interface
 * --------------------------------------------------
 **/
export interface IAMDashboardActionContext
  extends ActionContext<IAMDashboardState, IRootState> {}

/**
 * --------------------------------------------------
 *  Dashboard Actions Interface
 * --------------------------------------------------
 **/
export interface IAMDashboardActions
  extends ActionTree<IAMDashboardState, IRootState> {
  [AT.FETCH_SUBMISSION_REQUESTS](
    context: IAMDashboardActionContext,
    orgSubmissionsRequest: OrgSubmissionsRequest
  ): Promise<SubmissionRequest[]>;
  [AT.FETCH_ORGANISATION_CLIENTS](
    context: IAMDashboardActionContext,
    params: OrganisationClientRequest
  ): Promise<PensionScheme[]>;
  [AT.DOWNLOAD_AUTHORIZATION_LETTER](
    context: IAMDashboardActionContext,
    submissionRequestId: string
  ): Promise<AuthorizationLetterResponse>;
  [AT.DOWNLOAD_SUBMISSION_REQUESTS](
    context: IAMDashboardActionContext,
    organizationId: string
  ): Promise<DownloadSubmissionRequestsResponse>;
  [AT.FETCH_SUBMISSION_REQUESTS_URL](
    context: IAMDashboardActionContext,
    jobId: string
  ): Promise<FetchSubmissionRequestsUrlResponse>;
  [AT.SEARCH_FOR_SUBMISSION_REQUESTS](
    context: IAMDashboardActionContext,
    orgSubmissionsRequest: OrgSubmissionsRequest
  ): Promise<Paginated<SubmissionRequest>>;
  [AT.AUTO_MATCH_SUBMISSIONS](
    context: IAMDashboardActionContext,
    orgSubmissionsRequest: OrgSubmissionsRequest
  ): Promise<{
    templateContextData: ExtractTemplateContextResponse;
    autoMatchData: Record<string, SubmissionRequest>;
  }>;
}
